// ─────────────────────────────────────────────────────────────────────────────
// Platform Summary — Excel export
//
// Same feature as the standalone repo's PlatformSummaryPanel.tsx export:
// metadata header rows, styled column header, data rows with per-metric
// number formatting (percent / hours / count), a bold grand-total row,
// column widths, and the same Excel-formula-injection guard (a value
// starting with =, +, -, or @ gets a leading apostrophe so Excel treats it
// as text, not a formula).
//
// Implementation substitution (framework change, not a feature change):
// the old code used `exceljs`; this platform already depends on
// `xlsx-js-style` elsewhere, so this is rebuilt on that library instead of
// adding a second Excel dependency (agreed with Lakshay). One disclosed,
// minor difference: the old export froze the header row in the workbook
// view; xlsx-js-style's freeze-pane support is unreliable across Excel
// versions, so that specific cosmetic touch is dropped. The core feature —
// an accurate, correctly formatted, downloadable .xlsx of exactly what's on
// screen — is fully intact.
// ─────────────────────────────────────────────────────────────────────────────

import * as XLSX from 'xlsx-js-style';
import { PlatformSummaryMetrics } from '../services/automationInsightsService';

const EXCEL_INJECTION_PREFIX_PATTERN = /^\s*[=+\-@]/;

// Display-only label for ticket type — the underlying value stays "RITM"
// everywhere (API contract, SNOW enum, query params). Only what's written
// into the exported cells reads "SR", matching the on-screen table.
const TICKET_TYPE_DISPLAY_LABEL: Record<'ALL' | 'INC' | 'RITM', string> = {
  ALL: 'ALL',
  INC: 'INC',
  RITM: 'SR',
};

const HEADER_FILL = { fgColor: { rgb: 'F2F4F5' } };
const THIN_BORDER_COLOR = 'D0D7DE';
const DATA_BORDER_COLOR = 'E1E5E9';
const TOTAL_TOP_BORDER_COLOR = 'B8C0C7';

function thinBorder(color: string) {
  return {
    top: { style: 'thin', color: { rgb: color } },
    left: { style: 'thin', color: { rgb: color } },
    bottom: { style: 'thin', color: { rgb: color } },
    right: { style: 'thin', color: { rgb: color } },
  };
}

function sanitizeExcelText(value: string): string {
  if (!value) return value;
  return EXCEL_INJECTION_PREFIX_PATTERN.test(value) ? `'${value}` : value;
}

function formatExportTimestamp(date: Date): string {
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}_${pad(date.getHours())}-${pad(date.getMinutes())}`;
}

function formatHumanTimestamp(date: Date): string {
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export interface MetricPresentation {
  text: string;
  excelValue: number | string | null;
  excelNumFmt?: string;
}

export interface ExportColumn {
  id: string;
  label: string;
}

export interface PlatformSummaryExportSnapshot {
  generatedAt: Date;
  selectedTicketType: 'ALL' | 'INC' | 'RITM';
  selectedSourceFilter: string;
  /** 'created' | 'resolved' — which basis this export was generated under.
   * Optional so callers that don't have the basis toggle (none currently)
   * still compile. Added 2026-10. */
  selectedBasis?: string;
  showTicketTypeColumn: boolean;
  visibleMetricColumns: ExportColumn[];
  rows: Array<{ platform_key: string; platform_name: string; ticket_type: string } & PlatformSummaryMetrics>;
  grandTotal?: PlatformSummaryMetrics;
  dateFrom: string;
  dateTo: string;
  filterSummaryLabel: string;
  platformLabelByKey: Record<string, string>;
  getMetricPresentation: (columnId: string, value: number | undefined, row: PlatformSummaryMetrics) => MetricPresentation;
}

export function exportPlatformSummaryToExcel(snapshot: PlatformSummaryExportSnapshot): { fileName: string; rowCount: number } {
  const wb = XLSX.utils.book_new();
  const aoa: Array<Array<string | number | null>> = [];

  aoa.push(['Report', 'Platform Summary']);
  aoa.push(['Export Generated', formatHumanTimestamp(snapshot.generatedAt)]);
  aoa.push(['Current Ticket Type', TICKET_TYPE_DISPLAY_LABEL[snapshot.selectedTicketType] ?? snapshot.selectedTicketType]);
  if (snapshot.selectedBasis) {
    aoa.push(['Current Basis', snapshot.selectedBasis === 'resolved' ? 'Resolved' : 'Created']);
  }
  aoa.push(['Current Dashboard Filter', `${snapshot.filterSummaryLabel} | source: ${snapshot.selectedSourceFilter}`]);
  aoa.push(['Current Date Range', `${snapshot.dateFrom} to ${snapshot.dateTo}`]);
  aoa.push([]);

  const headerRowIndex = aoa.length;
  const headers = [
    'Platform',
    ...(snapshot.showTicketTypeColumn ? ['Ticket Type'] : []),
    ...snapshot.visibleMetricColumns.map((c) => c.label),
  ];
  aoa.push(headers);

  const dataStartRowIndex = aoa.length;
  snapshot.rows.forEach((row) => {
    const values: Array<string | number | null> = [
      sanitizeExcelText(snapshot.platformLabelByKey[row.platform_key] ?? row.platform_name),
      ...(snapshot.showTicketTypeColumn
        ? [sanitizeExcelText(TICKET_TYPE_DISPLAY_LABEL[row.ticket_type as 'ALL' | 'INC' | 'RITM'] ?? row.ticket_type)]
        : []),
    ];

    snapshot.visibleMetricColumns.forEach((column) => {
      const presentation = snapshot.getMetricPresentation(column.id, (row as any)[column.id], row);
      values.push(typeof presentation.excelValue === 'string' ? sanitizeExcelText(presentation.excelValue) : presentation.excelValue);
    });

    aoa.push(values);
  });
  const dataEndRowIndex = aoa.length - 1;

  let totalRowIndex: number | null = null;
  if (snapshot.grandTotal) {
    totalRowIndex = aoa.length;
    const totalValues: Array<string | number | null> = [
      'Grand total',
      ...(snapshot.showTicketTypeColumn ? ['ALL'] : []),
    ];

    snapshot.visibleMetricColumns.forEach((column) => {
      const presentation = snapshot.getMetricPresentation(column.id, (snapshot.grandTotal as any)?.[column.id], snapshot.grandTotal as PlatformSummaryMetrics);
      totalValues.push(typeof presentation.excelValue === 'string' ? sanitizeExcelText(presentation.excelValue) : presentation.excelValue);
    });

    aoa.push(totalValues);
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Style header row
  for (let c = 0; c < headers.length; c += 1) {
    const ref = XLSX.utils.encode_cell({ r: headerRowIndex, c });
    if (!ws[ref]) continue;
    ws[ref].s = {
      font: { bold: true },
      alignment: { vertical: 'center', horizontal: 'left' },
      fill: HEADER_FILL,
      border: thinBorder(THIN_BORDER_COLOR),
    };
  }

  const metricStartCol = snapshot.showTicketTypeColumn ? 2 : 1;

  // Style data rows + apply number formats
  for (let r = dataStartRowIndex; r <= dataEndRowIndex; r += 1) {
    const rowRecord = snapshot.rows[r - dataStartRowIndex];
    for (let c = 0; c < headers.length; c += 1) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!ws[ref]) continue;
      const isMetricCell = c >= metricStartCol;
      ws[ref].s = {
        alignment: { vertical: 'center', horizontal: isMetricCell ? 'right' : 'left' },
        border: thinBorder(DATA_BORDER_COLOR),
      };

      if (isMetricCell) {
        const column = snapshot.visibleMetricColumns[c - metricStartCol];
        const presentation = snapshot.getMetricPresentation(column.id, (rowRecord as any)[column.id], rowRecord);
        if (typeof presentation.excelValue === 'number' && presentation.excelNumFmt) {
          ws[ref].z = presentation.excelNumFmt;
        }
      }
    }
  }

  // Style grand total row
  if (totalRowIndex !== null) {
    for (let c = 0; c < headers.length; c += 1) {
      const ref = XLSX.utils.encode_cell({ r: totalRowIndex, c });
      if (!ws[ref]) continue;
      const isMetricCell = c >= metricStartCol;
      ws[ref].s = {
        font: { bold: true },
        alignment: { vertical: 'center', horizontal: isMetricCell ? 'right' : 'left' },
        border: {
          top: { style: 'medium', color: { rgb: TOTAL_TOP_BORDER_COLOR } },
          left: { style: 'thin', color: { rgb: DATA_BORDER_COLOR } },
          bottom: { style: 'thin', color: { rgb: DATA_BORDER_COLOR } },
          right: { style: 'thin', color: { rgb: DATA_BORDER_COLOR } },
        },
      };

      if (isMetricCell && snapshot.grandTotal) {
        const column = snapshot.visibleMetricColumns[c - metricStartCol];
        const presentation = snapshot.getMetricPresentation(column.id, (snapshot.grandTotal as any)[column.id], snapshot.grandTotal);
        if (typeof presentation.excelValue === 'number' && presentation.excelNumFmt) {
          ws[ref].z = presentation.excelNumFmt;
        }
      }
    }
  }

  // Column widths — simple sensible sizing (feature-equivalent, not the old
  // exact auto-fit algorithm): platform column wider, metric columns sized
  // to their header label.
  ws['!cols'] = headers.map((label, index) => ({
    wch: index === 0 ? 24 : Math.max(12, label.length + 4),
  }));

  XLSX.utils.book_append_sheet(wb, ws, 'Platform Summary');

  const fileName = `PlatformSummary_${snapshot.selectedTicketType}_${formatExportTimestamp(snapshot.generatedAt)}.xlsx`;
  XLSX.writeFile(wb, fileName);

  return { fileName, rowCount: snapshot.rows.length };
}
